import { useEffect, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Color, Fog } from 'three';
import type { AmbientLight, DirectionalLight, Material, MeshStandardMaterial } from 'three';
import type { RefObject } from 'react';
import type { TimeOfDayOption } from '../data/timeOfDayOptions';
import type { RuntimeWeather } from '../model/weatherTransition';

/**
 * 런타임 날씨를 씬 객체에 직접 적용하는 어댑터들.
 *
 * 날씨 전환 값을 props로 내려보내면 전환 샘플마다 트리가 재조정되고(절차적 배경에서는
 * 환경맵까지 다시 굽습니다) 프레임이 끊깁니다. 그래서 값은 ref 객체 하나로 공유하고,
 * 여기서 프레임마다 Three 객체의 필드만 덮어씁니다.
 */

/** 안개는 인스턴스를 유지하고 색·거리만 바꿉니다. args를 바꾸면 R3F가 객체를 재생성합니다. */
export function WeatherFog({
  weather,
  timeOfDay,
}: {
  weather: RuntimeWeather;
  timeOfDay: TimeOfDayOption;
}) {
  const scene = useThree((state) => state.scene);
  const fog = useMemo(() => new Fog(0xffffff, 1, 1000), []);
  const nightColor = useMemo(() => new Color(), []);

  useEffect(() => {
    const previous = scene.fog;
    scene.fog = fog;
    return () => {
      scene.fog = previous;
    };
  }, [scene, fog]);

  useFrame(() => {
    if (timeOfDay.isDark) {
      fog.color.set(nightColor.set(timeOfDay.skyColor));
    } else {
      fog.color.set(weather.fogColor);
    }
    fog.near = weather.fogNear;
    fog.far = weather.fogFar;
  });

  return null;
}

export interface WeatherLightsProps {
  weather: RuntimeWeather;
  timeOfDay: TimeOfDayOption;
  ambientRef: RefObject<AmbientLight | null>;
  keyLightRef: RefObject<DirectionalLight | null>;
  fillLightRef: RefObject<DirectionalLight | null>;
  /** 배경 프리셋이 정한 기준 광량입니다. 날씨·시간대 계수를 여기에 곱합니다. */
  baseAmbient: number;
  baseKey: number;
  baseFill: number;
}

/** 조명 세기는 매 프레임 계산해 넣습니다(전환 중에도 트리를 재조정하지 않습니다). */
export function WeatherLights({
  weather,
  timeOfDay,
  ambientRef,
  keyLightRef,
  fillLightRef,
  baseAmbient,
  baseKey,
  baseFill,
}: WeatherLightsProps) {
  useFrame(() => {
    const direct = weather.directLightMultiplier * timeOfDay.directMultiplier;
    if (ambientRef.current !== null) {
      ambientRef.current.intensity =
        baseAmbient * weather.ambientLightMultiplier * timeOfDay.ambientMultiplier;
    }
    if (keyLightRef.current !== null) {
      keyLightRef.current.intensity = baseKey * direct;
    }
    if (fillLightRef.current !== null) {
      fillLightRef.current.intensity = baseFill * direct;
    }
  });

  return null;
}

function isStandardMaterial(material: Material | Material[]): material is MeshStandardMaterial {
  return !Array.isArray(material) && 'roughness' in material;
}

export interface RoadMaterialTarget {
  /** 아스팔트 재질. 젖음에 따라 색·거칠기·반사가 바뀝니다. */
  asphalt?: RefObject<Material | Material[] | null>;
  /** 잔디·자갈 재질. 눈이 오면 밝아집니다. */
  ground?: RefObject<Material | Material[] | null>;
}

/**
 * 노면 재질을 날씨에 맞춥니다.
 *
 * 색·거칠기는 uniform 수준의 변경이라 프레임마다 써도 비용이 거의 없습니다. 반면 절차적
 * 텍스처는 그대로 재사용합니다(다시 만들면 512² 캔버스를 세 장 굽습니다).
 */
export function useWeatherRoadMaterials(
  weather: RuntimeWeather,
  targets: RoadMaterialTarget
): void {
  const dryAsphalt = useMemo(() => new Color('#ffffff'), []);
  const wetTint = useMemo(() => new Color(), []);
  const groundTint = useMemo(() => new Color(), []);

  useFrame(() => {
    const wetness = Math.min(1, Math.max(0, weather.roadWetness));
    const asphalt = targets.asphalt?.current;
    if (asphalt !== null && asphalt !== undefined && isStandardMaterial(asphalt)) {
      asphalt.color.copy(wetTint.set(weather.roadTint));
      asphalt.roughness = 0.72 - wetness * 0.46;
      asphalt.metalness = 0.04 + wetness * 0.05;
      asphalt.envMapIntensity = 0.35 + wetness * 1.15;
    }
    const ground = targets.ground?.current;
    if (ground !== null && ground !== undefined && isStandardMaterial(ground)) {
      ground.color.copy(
        groundTint.set(weather.snowy ? '#c7ccca' : wetness > 0.5 ? '#365241' : '#527346')
      );
    }
    void dryAsphalt;
  });
}
